// Create or edit one charge template (admin). The simple fields are form controls;
// the three structured ones (`match`, `lines`, `payer_overrides`) are JSON, edited
// as text, because their vocabulary is C's and C's service is the validator: this
// dialog only refuses text that is not JSON of the right kind, and shows C's 400
// message word for word for anything else.
//
// An edit sends the `version` it loaded; a stale save comes back 409 and is shown,
// never retried over someone else's change. A new template is created OFF, so it
// is tested before it touches a draft.

import React, { useEffect, useRef, useState } from "react";
import {
  BILL_TO,
  createTemplate,
  templatePayload,
  updateTemplate,
} from "./api";
import "./Charges.css";

const pretty = (value) => JSON.stringify(value, null, 2);

function parseJson(text, kind) {
  let value;
  try {
    value = JSON.parse(text);
  } catch (err) {
    return { error: "Not valid JSON." };
  }
  if (kind === "array" && !Array.isArray(value))
    return { error: "Must be a JSON list: [ … ]." };
  if (
    kind === "object" &&
    (!value || typeof value !== "object" || Array.isArray(value))
  ) {
    return { error: "Must be a JSON object: { … }." };
  }
  return { value };
}

export default function TemplateEditor({
  practiceId,
  template,
  onClose,
  onSaved,
}) {
  const isNew = !template;
  const [name, setName] = useState(template ? template.name : "");
  const [priority, setPriority] = useState(
    template && template.priority != null ? String(template.priority) : "100",
  );
  const [billTo, setBillTo] = useState(
    template ? template.billTo : "insurance",
  );
  const [requiresNote, setRequiresNote] = useState(
    template ? template.requiresNote : true,
  );
  const [doNotBill, setDoNotBill] = useState(
    template ? template.doNotBill : false,
  );
  const [matchText, setMatchText] = useState(
    pretty(template ? template.match : {}),
  );
  const [linesText, setLinesText] = useState(
    pretty(
      template
        ? template.lines
        : [
            {
              cpt: "99214",
              modifiers: [],
              units: 1,
              pos: "11",
              fee_schedule: true,
            },
          ],
    ),
  );
  const [overridesText, setOverridesText] = useState(
    pretty(template ? template.payerOverrides : {}),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);
  const nameRef = useRef(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !inFlight.current) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const match = parseJson(matchText, "object");
  const lines = parseJson(linesText, "array");
  const overrides = parseJson(overridesText, "object");
  const priorityValid = /^\d+$/.test(priority.trim());
  const valid =
    name.trim().length > 0 &&
    priorityValid &&
    !match.error &&
    !lines.error &&
    !overrides.error;

  const save = async () => {
    if (!valid || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const draft = {
      name: name.trim(),
      priority: Number(priority.trim()),
      match: match.value,
      lines: doNotBill ? [] : lines.value,
      billTo,
      payerOverrides: overrides.value,
      requiresNote,
      doNotBill,
      active: isNew ? false : template.active,
      version: template ? template.version : null,
    };
    try {
      const saved = isNew
        ? await createTemplate(practiceId, draft)
        : await updateTemplate(
            practiceId,
            template.id,
            templatePayload(draft, { includeVersion: true }),
          );
      onSaved(saved);
    } catch (err) {
      setError(
        err.status === 409
          ? "Someone changed this template since you opened it. Close, reload and edit again."
          : err.message,
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div
      className="chg-modal-backdrop"
      onClick={() => !inFlight.current && onClose()}
    >
      <div
        className="chg-modal chg-modal-wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chg-editor-title"
      >
        <div className="chg-modal-header">
          <h2 id="chg-editor-title">
            {isNew
              ? "New charge template"
              : `Edit ${template.name} (v${template.version})`}
          </h2>
          <button
            className="chg-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="chg-modal-body">
          {isNew && (
            <p className="chg-muted">
              Created off. Test it against a visit, then turn it on from the
              list.
            </p>
          )}
          <div className="chg-form-grid">
            <label className="chg-field">
              <span>Name</span>
              <input
                ref={nameRef}
                type="text"
                maxLength={120}
                value={name}
                disabled={busy}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="chg-field">
              <span>Priority (lower wins)</span>
              <input
                type="text"
                inputMode="numeric"
                value={priority}
                disabled={busy}
                onChange={(e) => setPriority(e.target.value)}
              />
              {!priorityValid && (
                <small className="chg-field-error">A whole number.</small>
              )}
            </label>
            <label className="chg-field">
              <span>Bill to</span>
              <select
                value={billTo}
                disabled={busy || doNotBill}
                onChange={(e) => setBillTo(e.target.value)}
              >
                {BILL_TO.map((b) => (
                  <option key={b.key} value={b.key}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="chg-check">
            <input
              type="checkbox"
              checked={requiresNote}
              disabled={busy}
              onChange={(e) => setRequiresNote(e.target.checked)}
            />
            <span>
              Needs a note — a visit without one goes to "needs a person"
            </span>
          </label>
          <label className="chg-check">
            <input
              type="checkbox"
              checked={doNotBill}
              disabled={busy}
              onChange={(e) => setDoNotBill(e.target.checked)}
            />
            <span>
              Do not bill — matching visits get no claim (e.g. workers' comp)
            </span>
          </label>

          <label className="chg-field">
            <span>Matches (JSON)</span>
            <textarea
              className="chg-json"
              rows={4}
              value={matchText}
              disabled={busy}
              spellCheck={false}
              onChange={(e) => setMatchText(e.target.value)}
            />
            <small className="chg-field-hint">
              e.g. {'{"source_color_in": ["green"]}'} · {"{}"} matches every
              visit. See docs/charge-capture.md for facts and operators.
            </small>
            {match.error && (
              <small className="chg-field-error">{match.error}</small>
            )}
          </label>
          {!doNotBill && (
            <label className="chg-field">
              <span>Lines (JSON)</span>
              <textarea
                className="chg-json"
                rows={6}
                value={linesText}
                disabled={busy}
                spellCheck={false}
                onChange={(e) => setLinesText(e.target.value)}
              />
              <small className="chg-field-hint">
                Each: cpt, modifiers, units, pos, and charge_cents or
                fee_schedule.
              </small>
              {lines.error && (
                <small className="chg-field-error">{lines.error}</small>
              )}
            </label>
          )}
          <label className="chg-field">
            <span>Payer rules (JSON)</span>
            <textarea
              className="chg-json"
              rows={3}
              value={overridesText}
              disabled={busy}
              spellCheck={false}
              onChange={(e) => setOverridesText(e.target.value)}
            />
            <small className="chg-field-hint">
              e.g. {'{"Blue Shield": {"add_modifier": "25"}}'}
            </small>
            {overrides.error && (
              <small className="chg-field-error">{overrides.error}</small>
            )}
          </label>

          {error && (
            <div className="chg-message error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="chg-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={save}
            disabled={!valid || busy}
          >
            {busy ? "Saving…" : isNew ? "Create (off)" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
